// VenueDashboardOffersPage.jsx — EV-524 Phase 4: merchant offer creation.
// Was a "coming soon" placeholder — now the real thing. "3 taps to a live
// offer": pick a curated offer type for YOUR business type (gym ≠ restaurant,
// offers.offer_type_venue_types matrix) → template-prefilled form per
// value_kind → publish. Plus list + publish/pause/archive of existing offers.
//
// API (Phase 2, Neon): GET /api/venue-dashboard/:slug (venue id + role) ·
// GET /api/me/venues/:id/offers (+venue_type) · GET /api/offers/types?venue_type= ·
// POST/PATCH /api/me/venues/:id/offers[/:offerId]

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';

const ORANGE = '#E46E34';
// 🔴 PG/JS weekday LAW: Sun=0..Sat=6 — Sunday FIRST in the toggle row.
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function token() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') || ''
  );
}
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

const STATUS_STYLE = {
  active:   { bg: '#dcfce7', fg: '#166534', labelKey: 'active' },
  draft:    { bg: '#f3f4f6', fg: '#6b7280', labelKey: 'draft' },
  paused:   { bg: '#fef3c7', fg: '#92400e', labelKey: 'paused' },
  expired:  { bg: '#fee2e2', fg: '#991b1b', labelKey: 'expired' },
  archived: { bg: '#f3f4f6', fg: '#9ca3af', labelKey: 'archived' },
};
const STATUS_LABEL_DEFAULT = { active: 'Active', draft: 'Draft', paused: 'Paused', expired: 'Expired', archived: 'Archived' };

function fmtValue(o, t) {
  if (o.value_percent) return t('vd.offValPercent', '{{n}}% off', { n: Number(o.value_percent) });
  if (o.value_amount_vnd) return t('vd.offValAmount', '{{n}}₫ off', { n: Number(o.value_amount_vnd).toLocaleString() });
  if (o.set_price_vnd) return t('vd.offValSetPrice', '{{n}}₫ set', { n: Number(o.set_price_vnd).toLocaleString() });
  if (o.free_item) return t('vd.offValFree', 'Free: {{item}}', { item: o.free_item });
  return o.type_label || '';
}

export default function VenueDashboardOffersPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { t } = useTranslation();
  const base = getApiBaseUrl();

  const [venue, setVenue] = useState(null);       // {id, role}
  const [venueType, setVenueType] = useState(null);
  const [offers, setOffers] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const vRes = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, { headers: authHeaders() });
      const vj = await vRes.json();
      const v = vj?.data || vj?.venue || vj;
      if (!v?.id) throw new Error(vj?.message || t('vd.venueNotFoundNoAccess', 'Venue not found / no access'));
      setVenue({ id: v.id, role: vj?.myRole || v.myRole || v.role });
      const oRes = await fetch(`${base}/me/venues/${v.id}/offers`, { headers: authHeaders() });
      const oj = await oRes.json();
      if (!oj?.success) throw new Error(oj?.message || t('vd.couldNotLoadOffers', 'Could not load offers'));
      setOffers(oj.offers || []);
      setVenueType(oj.venue_type || null);
      const tRes = await fetch(`${base}/offers/types${oj.venue_type ? `?venue_type=${encodeURIComponent(oj.venue_type)}` : ''}`);
      const tj = await tRes.json();
      setTypes(tj?.types || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [base, slug, t]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (offer, status) => {
    const r = await fetch(`${base}/me/venues/${venue.id}/offers/${offer.id}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }),
    });
    const j = await r.json();
    if (j?.success) setOffers((prev) => prev.map((o) => (o.id === offer.id ? { ...o, ...j.offer } : o)));
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', position: 'sticky', top: 0, zIndex: 100,
        background: 'linear-gradient(135deg, #f97316 0%, #fbbf24 100%)',
      }}>
        <button onClick={() => navigate(`/venue-dashboard/${slug}`)} style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
          color: 'white', fontSize: 20, width: 36, height: 36, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>←</button>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>{t('vd.offersPromos', 'Offers & Promos')}</span>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, maxWidth: 560, width: '100%', margin: '0 auto', padding: '14px 14px 90px' }}>
        {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>{t('common.loading', 'Loading…')}</div>}
        {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, padding: 14, fontSize: 13 }}>{err}</div>}

        {!loading && !err && (
          <>
            {!creating && (
              <button onClick={() => setCreating(true)} style={{
                width: '100%', background: `linear-gradient(135deg, ${ORANGE}, #c2410c)`,
                border: 'none', color: 'white', borderRadius: 14, padding: '15px 16px',
                fontSize: 16, fontWeight: 800, cursor: 'pointer', marginBottom: 14,
                boxShadow: '0 4px 14px rgba(228,110,52,0.3)',
              }}>
                🎁 {t('vd.createOffer', 'Create an offer')}
              </button>
            )}

            {creating && (
              <CreateWizard
                base={base} venue={venue} slug={slug} types={types} venueType={venueType}
                saving={saving} setSaving={setSaving}
                onClose={() => setCreating(false)}
                onCreated={(o) => { setOffers((p) => [o, ...p]); setCreating(false); }}
                t={t}
              />
            )}

            {offers.length === 0 && !creating && (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 12px', fontSize: 14 }}>
                {t('vd.noOffersYet', 'No offers yet — your first offer puts this venue on the deals map.')}
              </div>
            )}
            {offers.map((o) => {
              const st = STATUS_STYLE[o.status] || STATUS_STYLE.draft;
              return (
                <div key={o.id} style={{
                  background: 'white', border: '1px solid #e5e7eb', borderRadius: 14,
                  padding: 14, marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{o.type_icon || '🎁'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.title}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtValue(o, t)}{o.type_label ? ` · ${o.type_label}` : ''}</div>
                    </div>
                    <span style={{ background: st.bg, color: st.fg, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, flexShrink: 0 }}>{t(`vd.offerStatus_${st.labelKey}`, STATUS_LABEL_DEFAULT[st.labelKey])}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {o.status !== 'active' && o.status !== 'archived' && (
                      <button onClick={() => setStatus(o, 'active')} style={btnStyle('#16a34a')}>{t('vd.publish', 'Publish')}</button>
                    )}
                    {o.status === 'active' && (
                      <button onClick={() => setStatus(o, 'paused')} style={btnStyle('#b45309')}>{t('vd.pause', 'Pause')}</button>
                    )}
                    {o.status !== 'archived' && (
                      <button onClick={() => setStatus(o, 'archived')} style={btnStyle('#6b7280')}>{t('vd.archive', 'Archive')}</button>
                    )}
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 11, color: '#9ca3af', alignSelf: 'center' }}>
                      {o.current_redemptions || 0}{o.max_total_redemptions ? `/${o.max_total_redemptions}` : ''} {t('vd.redeemed', 'redeemed')}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      <DashboardNavBar />
    </div>
  );
}

const btnStyle = (color) => ({
  background: 'white', border: `1px solid ${color}`, color, borderRadius: 9,
  padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
});

// ── 3-step creation wizard ────────────────────────────────────────────────────
function CreateWizard({ base, venue, slug, types, venueType, saving, setSaving, onClose, onCreated, t }) {
  const [typeSlug, setTypeSlug] = useState(null);
  const type = types.find((x) => x.slug === typeSlug) || null;
  const cfg = (type && type.config) || {};

  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // EV-647 — load the venue's digital menu so the owner can scope the offer to
  // specific items. No menu → the offer just applies to the whole bill.
  const [menuItems, setMenuItems] = useState([]);
  useEffect(() => {
    if (!slug) return;
    fetch(`${base}/venues/${encodeURIComponent(slug)}/menu`)
      .then((r) => r.json())
      .then((j) => {
        const d = (j && j.data) || j || {};
        let items = [];
        const push = (it, section) => items.push({ id: it.id ?? it.item_name ?? it.name, name: it.name || it.item_name || '—', section: section || it.section_name || '' });
        if (Array.isArray(d.sections)) d.sections.forEach((s) => (s.items || []).forEach((it) => push(it, s.name || s.section_name)));
        else if (Array.isArray(d.items)) d.items.forEach((it) => push(it));
        else if (Array.isArray(d)) d.forEach((it) => push(it));
        setMenuItems(items.filter((x) => x.id != null));
      })
      .catch(() => {});
  }, [slug, base]);

  useEffect(() => {
    if (!type) return;
    setForm({
      title: type.example_copy || type.label,
      value_percent: cfg.default_percent ?? undefined,
      value_amount_vnd: cfg.default_amount_vnd ?? undefined,
      min_spend_vnd: cfg.default_min_spend_vnd ?? undefined,
      group_min_size: cfg.default_group_min ?? undefined,
      days_of_week: [0, 1, 2, 3, 4, 5, 6],
      time_slots: cfg.default_slots || [],
      first_time_only: type.slug === 'ot_first_timer',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeSlug]);

  const kind = type && type.value_kind;
  const needsPercent = ['percent', 'spend_threshold', 'time_slot', 'first_timer', 'item_percent', 'event_tied', 'last_hours', 'rainy_day', 'segment_rate', 'visit_streak'].includes(kind);
  const needsAmount = kind === 'fixed_amount';
  const needsMinSpend = kind === 'spend_threshold';
  const needsFreeItem = ['freebie', 'bogo', 'group_size'].includes(kind);
  const needsSetPrice = kind === 'set_price';
  const needsGroup = kind === 'group_size';
  const needsSlots = ['time_slot', 'last_hours'].includes(kind);

  const submit = async (publish) => {
    if (!type || !form.title) return;
    setSaving(true);
    try {
      const body = {
        offer_type_slug: type.slug,
        title: form.title,
        description: form.description || undefined,
        value_percent: needsPercent && form.value_percent ? Number(form.value_percent) : undefined,
        value_amount_vnd: needsAmount && form.value_amount_vnd ? Number(form.value_amount_vnd) : undefined,
        min_spend_vnd: needsMinSpend && form.min_spend_vnd ? Number(form.min_spend_vnd) : undefined,
        free_item: needsFreeItem ? form.free_item || undefined : undefined,
        set_price_vnd: needsSetPrice && form.set_price_vnd ? Number(form.set_price_vnd) : undefined,
        group_min_size: needsGroup && form.group_min_size ? Number(form.group_min_size) : undefined,
        days_of_week: form.days_of_week,
        time_slots: needsSlots ? form.time_slots : undefined,
        first_time_only: !!form.first_time_only,
        // EV-647 — menu items this offer applies to (empty = whole bill)
        item_scope: (form.item_scope && form.item_scope.length) ? form.item_scope : undefined,
        // EV-524 — audience / exclusivity
        eligibility: (form.eligibility && form.eligibility.length) ? form.eligibility : undefined,
        // EV-641 / EV-630 — booking kind + advance-claim gate
        booking_kind: form.booking_kind || undefined,
        claim_window_days: form.booking_kind === 'interest' && form.claim_window_days ? Number(form.claim_window_days) : undefined,
        requires_advance_claim: !!form.requires_advance_claim,
        status: publish ? 'active' : 'draft',
      };
      const r = await fetch(`${base}/me/venues/${venue.id}/offers`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!j || !j.success) throw new Error((j && j.message) || t('vd.saveFailed', 'Save failed'));
      onCreated(j.offer);
    } catch (e) {
      window.alert(e.message);
    } finally { setSaving(false); }
  };

  const input = {
    width: '100%', boxSizing: 'border-box', background: 'white',
    border: '1px solid #d1d5db', borderRadius: 10, padding: '11px 12px',
    fontSize: 14, color: '#111827', outline: 'none',
  };
  const label = { fontSize: 11.5, fontWeight: 700, color: '#6b7280', margin: '12px 0 5px' };

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: 16, marginBottom: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#111827', flex: 1 }}>
          {type ? `${type.icon} ${type.label}` : t('vd.pickOfferType', 'What kind of offer?')}
        </span>
        <button onClick={type ? () => setTypeSlug(null) : onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer' }}>✕</button>
      </div>

      {!type && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {venueType && <div style={{ fontSize: 11, color: '#9ca3af' }}>{t('vd.curatedFor', 'Curated for your business type')}</div>}
          {types.map((ty) => (
            <button key={ty.slug} onClick={() => setTypeSlug(ty.slug)} style={{
              display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
              background: ty.is_recommended ? '#fff7ed' : '#f9fafb',
              border: ty.is_recommended ? `1px solid ${ORANGE}55` : '1px solid #e5e7eb',
              borderRadius: 12, padding: '11px 13px', cursor: 'pointer',
            }}>
              <span style={{ fontSize: 20 }}>{ty.icon}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: '#111827' }}>
                  {ty.label}{ty.is_recommended ? <span style={{ color: ORANGE, fontSize: 11, marginLeft: 6 }}>★ {t('vd.recommended', 'recommended')}</span> : null}
                </span>
                {ty.example_copy && <span style={{ display: 'block', fontSize: 11.5, color: '#9ca3af' }}>{ty.example_copy}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      {type && (
        <>
          <div style={label}>{t('vd.offerTitle', 'Offer title (what customers see)')}</div>
          <input style={input} value={form.title || ''} onChange={(e) => set('title', e.target.value)} maxLength={120} />

          {needsPercent && (<>
            <div style={label}>{t('vd.percentOffMax', '% off (max 50)')}</div>
            <input style={input} type="number" min="1" max="50" value={form.value_percent ?? ''} onChange={(e) => set('value_percent', e.target.value)} />
          </>)}
          {needsAmount && (<>
            <div style={label}>{t('vd.amountOff', 'Amount off (VND)')}</div>
            <input style={input} type="number" value={form.value_amount_vnd ?? ''} onChange={(e) => set('value_amount_vnd', e.target.value)} />
          </>)}
          {needsMinSpend && (<>
            <div style={label}>{t('vd.minSpend', 'Minimum spend (VND)')}</div>
            <input style={input} type="number" value={form.min_spend_vnd ?? ''} onChange={(e) => set('min_spend_vnd', e.target.value)} />
          </>)}
          {needsFreeItem && (<>
            <div style={label}>{t('vd.freeItem', 'Free item / reward')}</div>
            <input style={input} value={form.free_item || ''} onChange={(e) => set('free_item', e.target.value)} placeholder={t('vd.freeItemPlaceholder', 'e.g. free dessert')} />
          </>)}
          {needsSetPrice && (<>
            <div style={label}>{t('vd.setPrice', 'Set price (VND)')}</div>
            <input style={input} type="number" value={form.set_price_vnd ?? ''} onChange={(e) => set('set_price_vnd', e.target.value)} />
          </>)}
          {needsGroup && (<>
            <div style={label}>{t('vd.minGroup', 'Minimum group size')}</div>
            <input style={input} type="number" min="2" value={form.group_min_size ?? ''} onChange={(e) => set('group_min_size', e.target.value)} />
          </>)}

          <div style={label}>{t('vd.validDays', 'Valid days')}</div>
          <div style={{ display: 'flex', gap: 5 }}>
            {DAYS.map((d, i) => {
              const on = (form.days_of_week || []).includes(i);
              return (
                <button key={d} onClick={() => set('days_of_week', on ? (form.days_of_week || []).filter((x) => x !== i) : [...(form.days_of_week || []), i].sort())} style={{
                  flex: 1, padding: '8px 0', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  background: on ? ORANGE : '#f3f4f6', color: on ? 'white' : '#9ca3af',
                  border: on ? `1px solid ${ORANGE}` : '1px solid #e5e7eb',
                }}>{d}</button>
              );
            })}
          </div>

          {needsSlots && (<>
            <div style={label}>{t('vd.timeWindow', 'Time window')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input style={{ ...input, width: 120 }} type="time" value={(form.time_slots && form.time_slots[0] && form.time_slots[0].start) || '16:00'}
                onChange={(e) => set('time_slots', [{ start: e.target.value, end: (form.time_slots && form.time_slots[0] && form.time_slots[0].end) || '19:00' }])} />
              <span style={{ color: '#9ca3af' }}>→</span>
              <input style={{ ...input, width: 120 }} type="time" value={(form.time_slots && form.time_slots[0] && form.time_slots[0].end) || '19:00'}
                onChange={(e) => set('time_slots', [{ start: (form.time_slots && form.time_slots[0] && form.time_slots[0].start) || '16:00', end: e.target.value }])} />
            </div>
          </>)}

          {/* EV-647 — scope the offer to specific MENU items (the digital-menu link) */}
          <div style={label}>{t('vd.appliesToItems', 'Applies to menu items (optional)')}</div>
          {menuItems.length === 0 ? (
            <div style={{ fontSize: 11.5, color: '#9ca3af' }}>
              {t('vd.noMenuYet', 'No digital menu yet — this offer applies to the whole bill. Add a menu to put offers on specific items.')}
            </div>
          ) : (
            <div style={{ maxHeight: 168, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, padding: 8 }}>
              <label style={{ display: 'flex', gap: 7, fontSize: 12.5, color: '#374151', marginBottom: 6, fontWeight: 700, cursor: 'pointer' }}>
                <input type="checkbox" checked={!(form.item_scope || []).length} onChange={() => set('item_scope', [])} />
                {t('vd.wholeBill', 'Whole bill / all items')}
              </label>
              {menuItems.map((it) => {
                const on = (form.item_scope || []).includes(it.id);
                return (
                  <label key={it.id} style={{ display: 'flex', gap: 7, fontSize: 12.5, color: '#374151', padding: '3px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={on} onChange={() => set('item_scope', on ? (form.item_scope || []).filter((x) => x !== it.id) : [...(form.item_scope || []), it.id])} />
                    <span>{it.name}{it.section ? <span style={{ color: '#9ca3af' }}> · {it.section}</span> : null}</span>
                  </label>
                );
              })}
            </div>
          )}

          {/* EV-524 — who can use this offer (everyone vs HonestLee-exclusive) */}
          <div style={label}>{t('vd.whoCanUse', 'Who can use this offer')}</div>
          <select style={input} value={(form.eligibility && form.eligibility[0]) || 'all'} onChange={(e) => set('eligibility', [e.target.value])}>
            <option value="all">{t('vd.everyone', 'Everyone')}</option>
            <option value="members">{t('vd.membersOnly', 'HonestLee members only')}</option>
            <option value="first_time">{t('vd.firstTimeOnly', 'First-time customers only')}</option>
          </select>

          {/* EV-641 / EV-630 — booking kind + advance-claim gate */}
          <div style={label}>{t('vd.bookingKind', 'How customers book this offer')}</div>
          <select style={input} value={form.booking_kind || ''} onChange={(e) => set('booking_kind', e.target.value)}>
            <option value="">{t('vd.bkInstant', 'Instant redeem (walk up & redeem)')}</option>
            <option value="interest">{t('vd.bkInterest', 'Interest → claim later')}</option>
            <option value="reservation">{t('vd.bkReservation', 'Reservation required')}</option>
          </select>
          {form.booking_kind === 'interest' && (
            <>
              <div style={label}>{t('vd.claimWindow', 'Claim window (days)')}</div>
              <input style={input} type="number" min="1" max="90" placeholder="7"
                value={form.claim_window_days ?? ''} onChange={(e) => set('claim_window_days', e.target.value)} />
            </>
          )}
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: '#374151', padding: '10px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.requires_advance_claim}
              onChange={(e) => {
                const on = e.target.checked;
                set('requires_advance_claim', on);
                if (on && form.booking_kind !== 'interest') set('booking_kind', 'interest');
              }} />
            <span>
              <b>{t('vd.requireAdvanceClaim', 'Must be claimed in advance')}</b>
              <span style={{ display: 'block', color: '#6b7280', fontSize: 11 }}>
                {t('vd.requireAdvanceClaimHint', 'Blocks walk-up redemption — customers opt in remotely first, then redeem at the venue.')}
              </span>
            </span>
          </label>

          <div style={label}>{t('vd.descOptional', 'Description (optional)')}</div>
          <input style={input} value={form.description || ''} onChange={(e) => set('description', e.target.value)} maxLength={600} />

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button disabled={saving} onClick={() => submit(false)} style={{ flex: 1, background: 'white', border: '1px solid #d1d5db', color: '#374151', borderRadius: 12, padding: '12px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {t('vd.saveDraft', 'Save draft')}
            </button>
            <button disabled={saving || !form.title} onClick={() => submit(true)} style={{ flex: 2, background: `linear-gradient(135deg, ${ORANGE}, #c2410c)`, border: 'none', color: 'white', borderRadius: 12, padding: '12px 0', fontSize: 14, fontWeight: 800, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? t('common.saving', 'Saving…') : `🚀 ${t('vd.publishNow', 'Publish now')}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
